import type { Board, Hint } from './types';
import { SIZE, boxOf, colOf, conflicts, isComplete, rowOf } from './board';
import { buildCandidates, findStep, solve, type Step } from './solver';

function cellLabel(i: number): string {
  return `row ${rowOf[i]! + 1}, column ${colOf[i]! + 1}`;
}

function unitDescription(step: Step): string {
  const first = step.unit![0]!;
  switch (step.unitKind) {
    case 'row':
      return `row ${rowOf[first]! + 1}`;
    case 'column':
      return `column ${colOf[first]! + 1}`;
    default:
      return `box ${boxOf[first]! + 1}`;
  }
}

/** Determine whether a set of cells lies along a row or a column. */
function lineKindOf(cells: number[]): 'row' | 'column' {
  const firstRow = rowOf[cells[0]!]!;
  return cells.every((i) => rowOf[i]! === firstRow) ? 'row' : 'column';
}

function describeStep(step: Step): { nudge: string; explanation: string } {
  const [d1, d2] = step.candidates;
  switch (step.technique) {
    case 'Naked single': {
      const i = step.placement!.index;
      return {
        nudge: `One cell in box ${boxOf[i]! + 1} has only a single possibility left.`,
        explanation: `The cell at ${cellLabel(i)} can only be ${d1}: every other digit already appears in its row, column, or box. Place ${d1} there.`,
      };
    }
    case 'Hidden single': {
      const i = step.placement!.index;
      const unit = unitDescription(step);
      return {
        nudge: `Take a close look at ${unit}.`,
        explanation: `Within ${unit}, the digit ${d1} can only go in the cell at ${cellLabel(i)} — no other cell in ${unit} can hold ${d1}, so it must go there.`,
      };
    }
    case 'Naked pair': {
      const unit = unitDescription(step);
      const [a, b] = step.cells;
      return {
        nudge: `Two cells in ${unit} share the same pair of candidates.`,
        explanation: `In ${unit}, the cells at ${cellLabel(a!)} and ${cellLabel(b!)} both allow only ${d1} and ${d2}. Those digits are locked into those two cells, so ${d1} and ${d2} can be removed from the other cells of ${unit}.`,
      };
    }
    case 'Hidden pair': {
      const unit = unitDescription(step);
      const [a, b] = step.cells;
      return {
        nudge: `Two digits in ${unit} are restricted to the same two cells.`,
        explanation: `In ${unit}, the digits ${d1} and ${d2} can only appear in the cells at ${cellLabel(a!)} and ${cellLabel(b!)}. Those cells must hold ${d1} and ${d2}, so every other candidate can be removed from them.`,
      };
    }
    case 'Pointing pair/triple': {
      const box = boxOf[step.cells[0]!]! + 1;
      const line = lineKindOf(step.eliminations.map((e) => e.index));
      return {
        nudge: `Look at where ${d1} can go inside box ${box}.`,
        explanation: `In box ${box}, the digit ${d1} is confined to a single ${line}. Wherever it lands it stays on that ${line}, so ${d1} can be removed from the cells of that ${line} outside the box.`,
      };
    }
    case 'Box/line reduction': {
      const unit = unitDescription(step);
      const box = boxOf[step.eliminations[0]!.index]! + 1;
      return {
        nudge: `Look at where ${d1} can go along ${unit}.`,
        explanation: `Along ${unit}, the digit ${d1} can only appear inside box ${box}. So ${d1} is pinned to that box on this line, and can be removed from the other cells of box ${box}.`,
      };
    }
    default:
      return { nudge: '', explanation: '' };
  }
}

function toHint(step: Step): Hint {
  const { nudge, explanation } = describeStep(step);
  const highlighted = new Set<number>(step.cells);
  for (const e of step.eliminations) highlighted.add(e.index);
  return {
    technique: step.technique,
    nudge,
    explanation,
    cells: [...highlighted],
    candidates: step.candidates,
    placement: step.placement,
  };
}

/**
 * Find the next single logical deduction from the in-progress board and return
 * a Hint that teaches it. Returns null only when the board is complete.
 */
export function nextHint(givens: Board, current: Board): Hint | null {
  void givens;
  if (isComplete(current)) return null;

  // Mistake: a filled cell conflicts with a peer.
  for (let i = 0; i < current.length; i++) {
    if (current[i] === 0) continue;
    const clash = conflicts(current, i);
    if (clash.length > 0) {
      return {
        technique: 'Mistake',
        nudge: 'Something is off — one of your entries breaks the rules.',
        explanation: `The ${current[i]} at ${cellLabel(i)} repeats in the same row, column, or box. Remove or correct one of the highlighted entries before continuing.`,
        cells: [i, ...clash],
        candidates: [current[i]!],
        placement: null,
      };
    }
  }

  // Mistake: no conflict, but the board can no longer be completed.
  if (solve(current) === null) {
    return {
      technique: 'Mistake',
      nudge: 'A previous entry has led to a dead end.',
      explanation:
        'No valid solution remains from the current board. One of your earlier placements must be wrong — try undoing recent moves.',
      cells: [],
      candidates: [],
      placement: null,
    };
  }

  const cand = buildCandidates(current);
  const step = findStep(current, cand);
  if (step) return toHint(step);

  // Solvable, but beyond the taught techniques: fall back to the solution's value
  // for the most-constrained empty cell so the player is never stuck.
  const solution = solve(current)!;
  let target = -1;
  let fewest = SIZE + 1;
  for (let i = 0; i < current.length; i++) {
    if (current[i] !== 0) continue;
    const size = cand[i]!.size;
    if (size < fewest) {
      fewest = size;
      target = i;
    }
  }
  const value = solution[target]!;
  return {
    technique: 'Advanced',
    nudge: 'This step needs reasoning beyond the basic techniques.',
    explanation: `The cell at ${cellLabel(target)} must be ${value}. Reaching this requires a chain of deductions past the standard singles, pairs, and line reductions.`,
    cells: [target],
    candidates: [value],
    placement: { index: target, value },
  };
}
